import { DatePipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../core/api.service';
import { AuthService } from '../core/auth.service';

interface UserItem {
  id: number;
  username: string;
  role: 'admin' | 'user';
  is_active: boolean;
  must_change_pw: boolean;
  zoho_email: string | null;
  zoho_subject: string | null;
  zoho_display_name: string | null;
  zoho_linked_at: string | null;
  last_login_at: string | null;
  password_updated_at: string | null;
  created_at: string | null;
}

interface AuthenticationControl {
  mode: 'local' | 'hybrid' | 'zoho';
  zohoConfigured: boolean;
  linkedUsers: number;
  linkedActiveAdmins: number;
}

@Component({
  selector: 'app-users',
  imports: [DatePipe, FormsModule, RouterLink],
  template: `
    <section class="card">
      <header class="head">
        <div>
          <h1>User Management</h1>
          <p>Admin users, Zoho identity links, and account status.</p>
        </div>
        @if (isAdmin()) {
          <div class="head-actions">
            <label class="inactive-toggle"><input type="checkbox" [ngModel]="showInactive()" (ngModelChange)="setShowInactive($event)"> Show inactive</label>
            <a class="button" routerLink="/users/new">+ New user</a>
          </div>
        }
      </header>

      @if (!isAdmin()) {
        <div class="msg err">Admin access required.</div>
      } @else {
        @if (error()) { <div class="msg err">{{ error() }}</div> }
        @if (ok()) { <div class="msg ok">{{ ok() }}</div> }

        <section class="auth-accordion">
          <button type="button" class="auth-accordion__trigger" (click)="toggleAuthenticationControl()" [attr.aria-expanded]="authenticationOpen()">
            <span>
              <strong>Application Sign-In</strong>
              <small>Choose which application login methods are available.</small>
            </span>
            <span class="mode-pill" [class.mode-pill--zoho]="authenticationControl()?.mode === 'zoho'">
              {{ authenticationControl()?.mode || 'Loading…' }}
            </span>
          </button>
          @if (authenticationOpen()) {
            <div class="auth-accordion__body">
              @if (authenticationError()) { <div class="msg err" role="alert">{{ authenticationError() }}</div> }
              @if (authenticationControl(); as control) {
                <p class="auth-summary">Zoho configured: <b>{{ control.zohoConfigured ? 'Yes' : 'No' }}</b> · Linked users: <b>{{ control.linkedUsers }}</b> · Linked active admins: <b>{{ control.linkedActiveAdmins }}</b></p>
                <label>Sign-in mode
                  <select [(ngModel)]="authenticationModeDraft">
                    <option value="local">Local password only</option>
                    <option value="hybrid">Zoho and local password (temporary)</option>
                    <option value="zoho">Zoho only</option>
                  </select>
                </label>
                @if (authenticationModeDraft === 'zoho') {
                  <div class="cutover-warning">Changing to Zoho only immediately signs every user out and disables local password sign-in. At least one active admin must already be linked to Zoho.</div>
                }
                @if (authenticationModeDraft !== 'local' && !control.zohoConfigured) {
                  <div class="msg err">Add the Zoho client ID, client secret, and redirect URI to the deployment before enabling Zoho.</div>
                }
                <div class="actions">
                  <button type="button" (click)="saveAuthenticationMode()" [disabled]="authenticationSaving() || authenticationModeDraft === control.mode || (authenticationModeDraft !== 'local' && !control.zohoConfigured)">
                    {{ authenticationSaving() ? 'Applying…' : 'Apply Sign-In Mode' }}
                  </button>
                </div>
              }
            </div>
          }
        </section>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th>Role</th>
                <th>Status</th>
                <th>Zoho email</th>
                <th>Zoho status</th>
                <th>Last Login</th>
                <th>PW Updated</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (u of visibleUsers(); track u.id) {
                <tr>
                  <td>{{ u.username }}</td>
                  <td>{{ u.role }}</td>
                  <td>{{ u.is_active ? 'Active' : 'Inactive' }}</td>
                  <td>{{ u.zoho_email || 'Not set' }}</td>
                  <td>{{ u.zoho_subject ? 'Linked' : 'Awaiting first Zoho sign-in' }}</td>
                  <td>{{ u.last_login_at ? (u.last_login_at | date:'yyyy-MM-dd HH:mm:ss') : '' }}</td>
                  <td>{{ u.password_updated_at ? (u.password_updated_at | date:'yyyy-MM-dd HH:mm:ss') : '' }}</td>
                  <td>{{ u.created_at ? (u.created_at | date:'yyyy-MM-dd HH:mm:ss') : '' }}</td>
                  <td class="actions">
                    @if (isLocalAuth()) {
                      <button type="button" (click)="resetPw(u)" [disabled]="!canResetPw(u)">Reset PW</button>
                    }
                    <button type="button" class="secondary" (click)="selectUser(u)">Manage</button>
                    @if (u.is_active) {
                      <button type="button" class="secondary" (click)="deactivate(u)" [disabled]="!canDeactivate(u)">Deactivate</button>
                    } @else {
                      <button type="button" class="secondary" (click)="reactivate(u)">Reactivate</button>
                    }
                  </td>
                </tr>
              } @empty {
                <tr><td colspan="9">No users found.</td></tr>
              }
            </tbody>
          </table>
        </div>

        @if (selectedUser()) {
          <section class="zoho-editor" aria-labelledby="zoho-identity-heading">
            <div>
              <p class="section-label">User access</p>
              <h2 id="zoho-identity-heading">Manage {{ selectedUser()!.username }}</h2>
              <p>Update role and, before first Zoho sign-in, the verified Zoho email used to establish the identity link.</p>
            </div>
            <label>Role
              <select [(ngModel)]="selectedRole" [disabled]="selectedUser()!.id === authUserId()">
                <option value="user">user</option>
                <option value="admin">admin</option>
              </select>
            </label>
            @if (selectedUser()!.zoho_subject) {
              <div class="identity-note">Zoho identity is linked. Its sign-in identifier cannot be changed here.</div>
            } @else {
              <label>Zoho email<input type="email" [(ngModel)]="selectedZohoEmail" autocomplete="email"></label>
            }
            @if (editorError()) { <div class="msg err" role="alert">{{ editorError() }}</div> }
            <div class="actions">
              <button type="button" (click)="saveUser()" [disabled]="savingUser()">{{ savingUser() ? 'Saving…' : 'Save User' }}</button>
              <button type="button" class="secondary" (click)="cancelUserEdit()">Cancel</button>
            </div>
          </section>
        } @else {
          <p class="edit-hint">Select “Manage” to prepare a user’s Zoho access or change their role.</p>
        }
      }
    </section>
  `,
  styles: `
    .card{background:#fff;border:1px solid var(--line);border-radius:16px;padding:16px;display:grid;gap:12px}
    .head,.head-actions{display:flex;justify-content:space-between;align-items:end;gap:12px;flex-wrap:wrap}.head-actions{align-items:center}
    h1{margin:0}
    p{margin:4px 0 0;color:var(--muted)}
    .button,button{height:38px;border:0;border-radius:10px;background:var(--primary);color:#fff;padding:0 12px;cursor:pointer;text-decoration:none;display:inline-flex;align-items:center}
    .secondary{background:#6b778c}
    .table-wrap{overflow:auto}
    table{width:100%;border-collapse:collapse;min-width:1180px}
    th,td{padding:10px;border-bottom:1px solid #edf0f6;text-align:left}
    .actions{display:flex;gap:8px;flex-wrap:wrap}
    .inactive-toggle{display:flex;align-items:center;gap:7px;font-weight:600;color:#42546f;font-size:.9rem}.inactive-toggle input{width:16px;height:16px}
    .auth-accordion{border:1px solid #c7ccd4;border-radius:12px;overflow:hidden;box-shadow:0 1px 6px rgba(0,0,0,.06)}.auth-accordion__trigger{width:100%;min-height:52px;border:0;background:#f5f8fc;color:#1f395f;padding:12px 14px;display:flex;justify-content:space-between;align-items:center;text-align:left;cursor:pointer}.auth-accordion__trigger small{display:block;margin-top:3px;color:#60748f;font-weight:400}.auth-accordion__body{padding:14px;display:grid;gap:12px}.auth-accordion__body label{display:grid;gap:6px;max-width:430px;font-weight:600;color:#42546f}.auth-accordion__body select{height:40px;border:1px solid #c7ccd4;border-radius:8px;padding:0 10px}.mode-pill{padding:4px 9px;border:1px solid #b8cfe8;border-radius:999px;background:#edf3ff;color:#1f4b7a;text-transform:capitalize;font-size:.8rem;font-weight:700}.mode-pill--zoho{border-color:#a5c8ba;background:#e8f7ef;color:#0f6f41}.auth-summary{margin:0;color:#42546f;font-size:.9rem}.cutover-warning{max-width:680px;background:#fffdf0;border:1px solid #e2c97a;border-radius:8px;padding:10px;color:#59460b;line-height:1.45}
    .zoho-editor{margin-top:4px;border:1px solid #c7ccd4;border-left:3px solid #f4be16;border-radius:12px;padding:16px;display:grid;gap:12px;max-width:620px;box-shadow:0 1px 6px rgba(0,0,0,.06)}
    .zoho-editor h2{margin:2px 0;color:#1f395f;font-size:1.05rem}.zoho-editor p{margin:0;color:#60748f}.zoho-editor label{display:grid;gap:6px;font-weight:600;color:#42546f}.zoho-editor input,.zoho-editor select{height:40px;border:1px solid #c7ccd4;border-radius:8px;padding:0 10px}.identity-note{background:#f5f8fc;border:1px solid #d8e0eb;border-radius:8px;padding:10px;color:#42546f}.section-label{border-left:3px solid #f4be16;padding-left:8px;font-size:.8rem!important;font-weight:700;color:#42546f!important;text-transform:uppercase;letter-spacing:.05em}.edit-hint{margin:0;color:#60748f;font-size:.85rem}
    .msg{padding:10px 12px;border-radius:10px}
    .err{background:#fdecec;color:#a42323}
    .ok{background:#e8f7ef;color:#0f6f41}
  `,
})
export class UsersComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  readonly users = signal<UserItem[]>([]);
  readonly visibleUsers = signal<UserItem[]>([]);
  readonly error = signal('');
  readonly ok = signal('');
  readonly isAdmin = signal(String(this.auth.user()?.role ?? '').toLowerCase() === 'admin');
  readonly isLocalAuth = computed(() => this.auth.authenticationMode() !== 'zoho');
  readonly selectedUser = signal<UserItem | null>(null);
  readonly savingUser = signal(false);
  readonly editorError = signal('');
  readonly showInactive = signal(false);
  readonly authenticationOpen = signal(false);
  readonly authenticationSaving = signal(false);
  readonly authenticationError = signal('');
  readonly authenticationControl = signal<AuthenticationControl | null>(null);
  selectedZohoEmail = '';
  selectedRole: 'admin' | 'user' = 'user';
  authenticationModeDraft: 'local' | 'hybrid' | 'zoho' = 'local';
  private okTimer: ReturnType<typeof setTimeout> | null = null;

  async ngOnInit(): Promise<void> {
    if (!this.isAdmin()) {
      this.error.set('Admin access required.');
      return;
    }
    await this.load();
    await this.loadAuthenticationControl();
  }

  async load(): Promise<void> {
    this.error.set('');
    try {
      const r = await firstValueFrom(this.api.get<{ users: UserItem[] }>('/users'));
      this.users.set(r.users ?? []);
      this.rebuildVisibleUsers();
    } catch {
      this.error.set('Failed to load users (admin required).');
    }
  }

  async resetPw(u: UserItem): Promise<void> {
    this.error.set('');
    this.ok.set('');
    if (u.id === this.auth.user()?.id) {
      this.error.set('Use Change Password for your own account.');
      return;
    }
    try {
      const r = await firstValueFrom(this.api.post<{ tempPassword: string }>(`/users/${u.id}/reset-password`, {}));
      this.showOk(`Temporary password for ${u.username}: ${r.tempPassword}`);
      await this.load();
    } catch {
      this.error.set('Reset password failed.');
    }
  }

  selectUser(u: UserItem): void {
    this.selectedUser.set(u);
    this.selectedZohoEmail = u.zoho_email ?? '';
    this.selectedRole = u.role;
    this.editorError.set('');
  }

  cancelUserEdit(): void {
    this.selectedUser.set(null);
    this.selectedZohoEmail = '';
    this.selectedRole = 'user';
    this.editorError.set('');
  }

  async saveUser(): Promise<void> {
    const user = this.selectedUser();
    if (!user) return;
    this.editorError.set('');
    this.savingUser.set(true);
    try {
      if (!user.zoho_subject && this.selectedZohoEmail.trim().toLowerCase() !== String(user.zoho_email ?? '').trim().toLowerCase()) {
        await firstValueFrom(this.api.put(`/users/${user.id}/zoho-identity`, { zohoEmail: this.selectedZohoEmail }));
      }
      if (this.selectedRole !== user.role) {
        await firstValueFrom(this.api.put(`/users/${user.id}/role`, { role: this.selectedRole }));
      }
      this.showOk(`User settings saved for ${user.username}.`);
      this.cancelUserEdit();
      await this.load();
    } catch {
      this.editorError.set('Unable to save this user. The Zoho email may be invalid or assigned to another user, or the role change is not allowed.');
    } finally {
      this.savingUser.set(false);
    }
  }

  setShowInactive(value: boolean): void {
    this.showInactive.set(value);
    this.rebuildVisibleUsers();
  }

  toggleAuthenticationControl(): void {
    this.authenticationOpen.set(!this.authenticationOpen());
  }

  async loadAuthenticationControl(): Promise<void> {
    this.authenticationError.set('');
    try {
      const result = await firstValueFrom(this.api.get<{ authentication: AuthenticationControl }>('/users/authentication'));
      this.authenticationControl.set(result.authentication);
      this.authenticationModeDraft = result.authentication.mode;
    } catch {
      this.authenticationError.set('Unable to load application sign-in settings.');
    }
  }

  async saveAuthenticationMode(): Promise<void> {
    this.authenticationError.set('');
    this.authenticationSaving.set(true);
    try {
      await firstValueFrom(this.api.put('/users/authentication', { mode: this.authenticationModeDraft }));
      window.location.assign('/login?auth=mode_changed');
    } catch {
      this.authenticationError.set('Unable to change sign-in mode. Confirm the Zoho deployment settings and linked administrator count.');
    } finally {
      this.authenticationSaving.set(false);
    }
  }

  authUserId(): number | undefined {
    return this.auth.user()?.id;
  }

  private rebuildVisibleUsers(): void {
    this.visibleUsers.set(this.showInactive() ? this.users() : this.users().filter((user) => user.is_active));
  }

  private showOk(message: string): void {
    this.ok.set(message);
    if (this.okTimer) clearTimeout(this.okTimer);
    this.okTimer = setTimeout(() => this.ok.set(''), 3000);
  }

  async deactivate(u: UserItem): Promise<void> {
    this.error.set('');
    this.ok.set('');
    if (u.id === this.auth.user()?.id) {
      this.error.set('You cannot deactivate your own account.');
      return;
    }
    if (u.role === 'admin' && this.activeAdminCount() <= 1) {
      this.error.set('Cannot deactivate the last active admin.');
      return;
    }
    try {
      await firstValueFrom(this.api.post(`/users/${u.id}/deactivate`, {}));
      this.showOk('User deactivated.');
      await this.load();
    } catch {
      this.error.set('Deactivate failed.');
    }
  }

  async reactivate(u: UserItem): Promise<void> {
    this.error.set('');
    this.ok.set('');
    try {
      await firstValueFrom(this.api.post(`/users/${u.id}/reactivate`, {}));
      this.showOk('User reactivated.');
      await this.load();
    } catch {
      this.error.set('Reactivate failed.');
    }
  }

  activeAdminCount(): number {
    return this.users().filter((user) => user.role === 'admin' && user.is_active).length;
  }

  canResetPw(u: UserItem): boolean {
    return u.id !== this.auth.user()?.id;
  }

  canDeactivate(u: UserItem): boolean {
    if (!u.is_active) return false;
    if (u.id === this.auth.user()?.id) return false;
    if (u.role === 'admin' && this.activeAdminCount() <= 1) return false;
    return true;
  }
}
